import { useCallback, useEffect, useRef, useState } from 'react';
import useCardGestures from '../../application/motion/useCardGestures';
import { useLocale } from '../../application/i18n/LocaleProvider';
import { plantSlides } from '../../domain/plants/plantMedia';
import { ExpandIcon } from '../icons/ControlIcons';
import { HeartIcon, ShareIcon } from '../chrome/BrandIcons';
import PlantLightbox from './PlantLightbox';

/**
 * The hero of the approved detail reference: the plant edge to edge, and the
 * round controls that belong to the plant — saving it and sharing it. The way
 * back sits in the same row and is drawn the same way, but it belongs to the
 * page: it has to outlast this band on the way down.
 *
 * The plant's name and tagline used to hang over the bottom of this band. They
 * are `PlantBrief`'s now (SPEC-024 D-2), and what is left here is the picture:
 * the band is the top part of the stage, sized by what the words below it left
 * over rather than by a `vh` of its own.
 *
 * ONE picture, and the way into the rest (SPEC-025 D-8 as revised).
 *
 * The scroll-snap rail this band was built on (SPEC-003, SPEC-006) is gone, and
 * so is everything that replaced it: the tap target, the counter chip, the
 * band's own sideways drag, the dots. The band shows the plant's lead picture and
 * answers nothing.
 *
 * The carousel lives in ONE place now — the full-screen viewer, behind the zoom
 * chip, where the rail is real, the dots report a real scroll position, and a
 * horizontal swipe cannot be confused with anything else because there is nothing
 * else on that screen (SPEC-006).
 *
 * That is what hands the card its gesture back. The band is the largest surface
 * on a card whose whole point is being dealt sideways, and every arrangement
 * where it answered a drag of its own split one gesture across two halves of one
 * object, separated by a rule the reader cannot see.
 *
 * The viewer still hands back the slide it was left on, which is the one piece of
 * SPEC-006 D-2 that survives all of this: close the gallery on the leaf and the
 * card is showing the leaf. It is also the only way the band's picture changes.
 */

/** How far a press may travel and still be a tap rather than a swipe, in CSS px. */
const TAP_SLOP = 10;

export default function PlantHero({ plant, isFront = true, isSaved, onToggleSaved, onShare }) {
    const { t } = useLocale();
    const pressOriginRef = useRef(null);
    const [activeSlide, setActiveSlide] = useState(0);
    const [turn, setTurn] = useState(null);
    const [isViewerOpen, setViewerOpen] = useState(false);

    const slides = plantSlides(plant);

    // A card dealt starts on its own lead picture. This was a `key` on the
    // component until the flicker it caused was traced back to it: remounting
    // the band threw away the <img> the reader was already looking at, which is
    // the very picture the deal had just revealed behind the departing card.
    useEffect(() => {
        setActiveSlide(0);
        setTurn(null);
    }, [plant.id]);

    const commonName = plant.prose?.commonName ?? plant.id;
    const slide = slides[activeSlide] ?? slides[0] ?? null;

    /**
     * The band's own sideways: the plant's other pictures (SPEC-025 D-39).
     *
     * Wraps, unlike the deck it sits on. Four pictures of one plant are a loop a
     * reader is idly turning, and there is no end of the pictures worth
     * defending — where a category has a first and a last that mean something.
     *
     * `forward` is a drag LEFT, which brings the next picture in from the right:
     * the same reading the card below gives the same gesture, one list down.
     */
    const turnPicture = useCallback((direction) => {
        const total = Math.max(slides.length, 1);

        // Remembered so the incoming picture can come in from the side the
        // gesture asked for (D-41). Cleared when the PLANT changes, so a card
        // being dealt never slides its picture — the deal's whole promise is that
        // the picture was already there and does not move.
        setTurn(direction);
        setActiveSlide((current) => (current + (direction === 'forward' ? 1 : -1) + total) % total);
    }, [slides.length]);

    const { handlers: bandHandlers } = useCardGestures({ onStep: turnPicture });

    const rememberPress = (event) => {
        pressOriginRef.current = { x: event.clientX, y: event.clientY };
    };

    /**
     * Was this a tap, or the beginning of a swipe that happened to land here?
     *
     * The chips sit on top of the card's own sideways gesture, and a press that
     * travels is the deck's. Cleared on every activation, so a keyboard press —
     * which arrives with no pointer origin at all — is never judged against a
     * stale one.
     */
    const wasTap = (event) => {
        const origin = pressOriginRef.current;

        pressOriginRef.current = null;

        return !origin || Math.hypot(event.clientX - origin.x, event.clientY - origin.y) <= TAP_SLOP;
    };

    // Wraps, unlike the deck it sits on (SPEC-025 D-2 and D-8 are deliberately
    // different): four pictures of one plant are a loop a reader is idly
    // cycling, and there is no end of the pictures worth defending.
    const openFullScreen = (event) => {
        if (wasTap(event)) {
            setViewerOpen(true);
        }
    };

    const closeFullScreen = useCallback((leftOn) => {
        // The band lands on the picture the eye left rather than the one it
        // started on. As far as the reader is concerned the band was always here.
        setViewerOpen(false);
        setActiveSlide(leftOn);
    }, []);

    return (
        // `--front` distinguishes this band from the copy on the back of the
        // card behind, which is built from the same classes so that one set of
        // rules lays both out (SPEC-025 D-25). Nothing styles it; it is what
        // lets anything ASK for the band a reader is actually looking at.
        <header className={`plant-hero${isFront ? ' plant-hero--front' : ''}`}>
            {/* THE BAND ANSWERS ITS OWN SIDEWAYS: a drag across the picture
                turns the plant's pictures, where a drag on the paper below deals
                the next plant (SPEC-025 D-39).

                It says so with `data-sideways`, which `isSidewaysClaimed` reads,
                rather than by being a scroller the card's gesture happens to
                notice — one picture is mounted and swapped, so there is nothing
                here to scroll and nothing for the old computed rule to see.

                Only on the card in hand. A seat behind it is `inert`, so its
                band never receives a pointer at all. */}
            <div
                className="plant-hero__band"
                data-sideways={isFront ? 'own' : undefined}
                aria-hidden={slide ? undefined : true}
                {...(isFront ? bandHandlers : {})}
            >
                {slide ? (
                    <div
                        className={[
                            'plant-hero__slide',
                            `plant-hero__slide--${slide.kind}`,
                            turn ? `plant-hero__slide--from-${turn}` : '',
                        ].filter(Boolean).join(' ')}
                        key={slide.key}
                    >
                        <img
                            // Load-bearing rather than tidiness: an <img> is
                            // natively draggable, so a mouse press that lands on
                            // the picture and moves starts a drag the browser
                            // owns, and the card's gesture never sees the moves.
                            // Taken from the habb.me pwa's card.
                            draggable={false}
                            src={slide.src}
                            // The BAND's candidates, not the viewer's. This is a
                            // strip on a card; the native-width rung the ingest
                            // emits is for the screen one tap away, and offering
                            // it here would fetch up to 900 KB for a picture
                            // about to be drawn small (SPEC-017 PR-17.7).
                            srcSet={slide.bandSrcSet ?? undefined}
                            sizes="(min-width: 640px) 640px, 100vw"
                            alt={t(slide.altKey, slide.altValues)}
                            // The first slide is the largest paint on the screen
                            // and must not wait for anything.
                            loading={activeSlide === 0 ? 'eager' : 'lazy'}
                            decoding="async"
                        />
                    </div>
                ) : null}
            </div>

            {/* WHERE THE READER IS IN THE PICTURES, drawn with the very
                component the full-screen viewer draws (SPEC-025 D-40).

                `.page-dots` in foundations.css is the indicator; placement is the
                block's own. Two renderings of one idea rather than two ideas —
                and the viewer is one press away from here, so a reader meets the
                same mark in both places rather than learning two.

                An indicator, never a control: the band's swipe is the mechanism,
                and dots that could also be pressed would be a second way of doing
                one thing. `aria-hidden`, and transparent to pointers, so the drag
                it reports on lands on the band underneath it. */}
            {isFront && slides.length > 1 ? (
                <ol className="plant-hero__dots page-dots" aria-hidden="true">
                    {slides.map((each, index) => (
                        <li
                            className={`page-dot${index === activeSlide ? ' page-dot--active' : ''}`}
                            key={each.key}
                        />
                    ))}
                </ol>
            ) : null}

            {/* Only the top fade now: the corner wash existed so type could be
                read over a photograph, and the type has moved below the picture
                (SPEC-024 D-2). The round controls still float on it. */}
            {/* The chrome is on EVERY seated card, not just the one in hand.

                Gated to the front card it all APPEARED at the swap — the scrim
                is a gradient across the top of the picture, and it landed on
                the image the instant a seat was promoted. The picture had not
                moved and had not been rebuilt; furniture arrived over it, which
                is what a reader sees as the picture repainting (D-33).

                Nothing here is reachable on a seat that is not in hand: the
                seat is `inert` and `aria-hidden`, so these are pixels and
                nothing more. The habb.me pwa renders its whole card in every
                seat for the same reason, with its handlers made no-ops. */}
            <div className="plant-hero__scrim" />

            {/* Two controls, not three: the way back is the page's now, so it
                can stay put when the hero scrolls away (SPEC-003 PR-3.10), and
                it floats into the space this row leaves on the left. */}
            <div className="plant-hero__actions">
                <div className="plant-hero__actions-end">
                    <button
                        type="button"
                        className="round-button"
                        onClick={onToggleSaved}
                        aria-pressed={isSaved}
                        aria-label={t(isSaved ? 'plants.detail.unsave' : 'plants.detail.save')}
                    >
                        <HeartIcon size={22} filled={isSaved} />
                    </button>
                    {/* The glyph opens a CHOICE rather than performing one
                        (SPEC-019 D-3). It handed over the A4 sheet for every
                        destination until now — the right file for a Photos roll,
                        the wrong one for a feed, and no file at all for the
                        places where an address unfurls into the plant itself. */}
                    <button
                        type="button"
                        className="round-button"
                        onClick={onShare}
                        aria-label={t('plants.detail.share')}
                    >
                        <ShareIcon size={22} />
                    </button>
                </div>
            </div>

            {/* The way into the picture, in the corner it has always been drawn
                in. It used to be a decorative cue over a picture that was itself
                the button; now the picture is not a button and this is (D-8). */}
            {slide ? (
                <button
                    type="button"
                    className="plant-hero__zoom"
                    onPointerDown={rememberPress}
                    onClick={openFullScreen}
                    aria-label={t('plants.detail.viewFullScreen')}
                >
                    <ExpandIcon size={18} />
                </button>
            ) : null}

            {isViewerOpen ? (
                <PlantLightbox
                    slides={slides}
                    startIndex={activeSlide}
                    plantName={commonName}
                    onClose={closeFullScreen}
                />
            ) : null}

        </header>
    );
}
